import React from 'react';
import {
    HiOutlineIdentification,
    HiOutlinePencilAlt,
    HiOutlinePaperAirplane,
    HiOutlineVariable,
    HiOutlineClipboardCheck,
    HiCheckCircle,
} from 'react-icons/hi';
import { StudioIssue, StudioSection, countBySection } from './studioIssues';

export const STUDIO_SECTIONS: { key: StudioSection; label: string; hint: string; icon: React.ReactNode }[] = [
    { key: 'identity', label: 'Identificação', hint: 'Nome, grupo e descrição', icon: <HiOutlineIdentification size={18} /> },
    { key: 'content', label: 'Conteúdo', hint: 'O que a mensagem diz', icon: <HiOutlinePencilAlt size={18} /> },
    { key: 'delivery', label: 'Envio', hint: 'De onde e para quem', icon: <HiOutlinePaperAirplane size={18} className="rotate-90" /> },
    { key: 'variables', label: 'Variáveis', hint: 'Campos preenchidos no envio', icon: <HiOutlineVariable size={18} /> },
    { key: 'review', label: 'Revisão', hint: 'Pendências e status', icon: <HiOutlineClipboardCheck size={18} /> },
];

interface StudioNavProps {
    current: StudioSection;
    issues: StudioIssue[];
    onSelect: (section: StudioSection) => void;
    variableCount: number;
}

const Indicator: React.FC<{ errors: number; warnings: number; showOk: boolean }> = ({ errors, warnings, showOk }) => {
    if (errors) return <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold flex items-center justify-center">{errors}</span>;
    if (warnings) return <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-black text-[11px] font-semibold flex items-center justify-center">{warnings}</span>;
    return showOk ? <HiCheckCircle className="text-emerald-500" size={18} /> : null;
};

const StudioNav: React.FC<StudioNavProps> = ({ current, issues, onSelect, variableCount }) => {
    const totalErrors = issues.filter((i) => i.level === 'error').length;
    return (
        <nav className="flex flex-col gap-1">
            {STUDIO_SECTIONS.map((s, index) => {
                const active = current === s.key;
                const errors = s.key === 'review' ? totalErrors : countBySection(issues, s.key, 'error');
                const warnings = s.key === 'review' ? 0 : countBySection(issues, s.key, 'warning');
                return (
                    <button
                        key={s.key}
                        type="button"
                        onClick={() => onSelect(s.key)}
                        className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                            active
                                ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                    >
                        <span
                            className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                active ? 'bg-teal-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-gray-500 dark:text-gray-300'
                            }`}
                        >
                            {s.icon}
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium">
                                <span className="text-gray-400 mr-1">{index + 1}.</span>
                                {s.label}
                                {s.key === 'variables' && <span className="ml-1 text-xs text-gray-400">({variableCount})</span>}
                            </span>
                            <span className="block text-[11px] text-gray-400 truncate">{s.hint}</span>
                        </span>
                        <Indicator errors={errors} warnings={warnings} showOk={s.key !== 'review'} />
                    </button>
                );
            })}
        </nav>
    );
};

export default StudioNav;
